import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { auditApi } from '@/lib/api'
import type { AuditLog } from '@/types'
import {
  ShieldCheck,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { format } from 'date-fns'
import { pt } from 'date-fns/locale'

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  login: 'bg-gray-100 text-gray-700',
  logout: 'bg-gray-100 text-gray-700',
  register: 'bg-purple-100 text-purple-800',
  'password-reset': 'bg-orange-100 text-orange-800',
}

function actionBadge(action: string) {
  const cls = ACTION_COLORS[action.toLowerCase()] ?? 'bg-yellow-100 text-yellow-800'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {action}
    </span>
  )
}

function ChangesCell({ value }: { value: string | null }) {
  const [open, setOpen] = useState(false)
  if (!value) return <span className="text-gray-400 text-xs">—</span>

  let parsed: unknown = null
  try { parsed = JSON.parse(value) } catch { /* raw string */ }

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
      >
        Ver alterações {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <pre className="mt-1 text-xs bg-gray-50 border rounded p-2 max-w-sm overflow-auto whitespace-pre-wrap break-all">
          {parsed !== null ? JSON.stringify(parsed, null, 2) : value}
        </pre>
      )}
    </div>
  )
}

export default function AuditPage() {
  const [page, setPage] = useState(1)
  const pageSize = 50

  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    search: '',
    date_from: '',
    date_to: '',
  })
  const [applied, setApplied] = useState(filters)

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['audit-logs', page, applied],
    queryFn: () =>
      auditApi.list({
        page,
        page_size: pageSize,
        action: applied.action || undefined,
        entity_type: applied.entity_type || undefined,
        search: applied.search || undefined,
        date_from: applied.date_from || undefined,
        date_to: applied.date_to || undefined,
      }),
    placeholderData: (prev) => prev,
  })

  const { data: actions } = useQuery({
    queryKey: ['audit-actions'],
    queryFn: () => auditApi.getActions().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })

  const { data: entityTypes } = useQuery({
    queryKey: ['audit-entity-types'],
    queryFn: () => auditApi.getEntityTypes().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })

  function applyFilters() {
    setPage(1)
    setApplied(filters)
  }

  function clearFilters() {
    const empty = { action: '', entity_type: '', search: '', date_from: '', date_to: '' }
    setFilters(empty)
    setApplied(empty)
    setPage(1)
  }

  const logs: AuditLog[] = data?.data?.items ?? []
  const total = data?.data?.total ?? 0
  const totalPages = data?.data?.total_pages ?? 1

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-primary-600" size={22} />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Registo de Auditoria</h1>
            <p className="text-sm text-gray-500">Todas as atividades registadas no sistema</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 text-gray-600"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Pesquisar descrição..."
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
              className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* Action */}
          <select
            value={filters.action}
            onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
            className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 text-gray-700"
          >
            <option value="">Todas as ações</option>
            {(actions ?? []).map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>

          {/* Entity type */}
          <select
            value={filters.entity_type}
            onChange={e => setFilters(f => ({ ...f, entity_type: e.target.value }))}
            className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 text-gray-700"
          >
            <option value="">Todas as entidades</option>
            {(entityTypes ?? []).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          {/* Date from */}
          <input
            type="datetime-local"
            value={filters.date_from}
            onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
            className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 text-gray-700"
          />

          {/* Date to */}
          <input
            type="datetime-local"
            value={filters.date_to}
            onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
            className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 text-gray-700"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={applyFilters}
            className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            Filtrar
          </button>
          <button
            onClick={clearFilters}
            className="px-4 py-1.5 text-sm border rounded-lg hover:bg-gray-50 text-gray-600"
          >
            Limpar
          </button>
          {total > 0 && (
            <span className="text-xs text-gray-500 ml-auto">{total.toLocaleString()} registos</span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-48 text-gray-500 text-sm">A carregar...</div>
        ) : logs.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Sem registos de auditoria</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Data/Hora</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilizador</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ação</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entidade</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alterações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-500">
                      {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: pt })}
                    </td>
                    <td className="px-4 py-2.5">
                      {log.user_email ? (
                        <div>
                          <div className="font-medium text-gray-900 text-xs">{log.user_name ?? log.user_email}</div>
                          <div className="text-gray-400 text-xs">{log.user_name ? log.user_email : ''}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">Sistema</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                      {log.ip_address ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {actionBadge(log.action)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-700 whitespace-nowrap">
                      {log.entity_type}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                      {log.entity_id ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 max-w-xs">
                      <span className="line-clamp-2">{log.description ?? '—'}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <ChangesCell value={log.changes} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Página {page} de {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
